// ClientApp/src/hooks/useApi.js
// ---------------------------------------------------------------------------
// Tiny fetch helpers (procurement pattern). The Vite dev server proxies
// /api → ASP.NET, so relative URLs work in dev and production. Controllers
// return camelCase JSON; the cookie rides along via credentials: same-origin.
// ---------------------------------------------------------------------------

async function request(path, init = {}) {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { Accept: "application/json", ...(init.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let body = null;
    const text = await res.text().catch(() => "");
    try { body = text ? JSON.parse(text) : null; } catch { body = null; }
    const err = new Error(`HTTP ${res.status} ${res.statusText} — ${text}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  get: (path) => request(path),
  post: (path, body) =>
    request(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body == null ? undefined : JSON.stringify(body),
    }),
  put: (path, body) =>
    request(path, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: body == null ? undefined : JSON.stringify(body),
    }),
  del: (path) => request(path, { method: "DELETE" }),
};

export const AuthApi = {
  me: () => api.get("/api/auth/me"),
  login: (email, password) => api.post("/api/auth/login", { email, password }),
  logout: () => api.post("/api/auth/logout"),
  // Forgot password: TMCHub emails a link to /reset-password; reset sets it and signs in.
  forgot: (email) => api.post("/api/auth/forgot", { email }),
  reset: (token, password) => api.post("/api/auth/reset", { token, password }),
  // Sign-up → a pending account staff approve; the approval email's one-time link signs them in.
  register: (form) => api.post("/api/auth/register", form),
  signupOptions: (code) => api.get(`/api/auth/signup-options?c=${encodeURIComponent(code)}`), // a client's link: { clientName, products }
  link: (token) => api.post("/api/auth/link", { token }),
  // Development helper — issues a cookie without TMCHub (404 in prod).
  devSignIn: (email, role) => api.post(`/api/auth/dev-signin?email=${encodeURIComponent(email)}&role=${role}`),
  // Guest join: a link-scoped cookie for one meeting (no TMCHub account needed).
  guest: (code, displayName) => api.post(`/api/auth/guest?code=${encodeURIComponent(code)}`, { displayName }),
};

// The signed-in user's own profile (display name + avatar), persisted on TMCHub.
export const MeApi = {
  getProfile: () => api.get("/api/me/profile"),
  updateProfile: (body) => api.post("/api/me/profile", body), // { displayName?, avatarUrl? }
};

// Server cap (MediaController.MaxBytes). Kept in sync so the client can pre-check and give a clear
// message instead of firing a doomed upload that fails silently.
export const MEDIA_MAX_BYTES = 100 * 1024 * 1024;

export const MediaApi = {
  // Multipart upload via XHR (not fetch) so we get real upload-progress events — a large video can
  // take a while and otherwise looks stuck. onProgress receives a fraction 0..1 (only when the
  // browser reports a computable length). Rejects with a human-readable message on failure.
  upload: (file, onProgress) => new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("file", file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/media");
    xhr.withCredentials = true; // same-origin auth cookie
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => { if (e.lengthComputable && e.total) onProgress(e.loaded / e.total); };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } // { url, name, contentType }
        catch { reject(new Error("Upload succeeded but the response was unreadable.")); }
        return;
      }
      // Surface the server's message (e.g. "File too large (max 30 MB)."); Cloud Run rejects an
      // over-limit body with 413 before our handler even runs.
      let msg = xhr.status === 413 ? "File is too large to send (max 100 MB)." : `Upload failed (${xhr.status}).`;
      try { const j = JSON.parse(xhr.responseText); if (j && j.error) msg = j.error; } catch { /* keep default */ }
      reject(new Error(msg));
    };
    xhr.onerror = () => reject(new Error("Upload failed — check your connection and try again."));
    xhr.ontimeout = () => reject(new Error("Upload timed out — the file may be too large or the connection too slow."));
    xhr.send(form);
  }),

  // Large-file path: upload the bytes DIRECTLY to storage (GCS resumable session) so they bypass our
  // request path and Cloud Run's 32 MiB body limit. Falls back to the multipart upload() when the
  // server reports no direct target (local dev). Returns the same { url, name, contentType } shape.
  uploadDirect: async (file, onProgress) => {
    let ticket;
    try {
      const res = await fetch("/api/media/direct-upload", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ fileName: file.name, contentType: file.type || "application/octet-stream", size: file.size }),
      });
      if (!res.ok) {
        let msg = `Upload failed (${res.status}).`;
        try { const j = await res.json(); if (j && j.error) msg = j.error; } catch { /* keep default */ }
        throw new Error(msg);
      }
      ticket = await res.json();
    } catch (e) {
      // If we can't even reach the ticket endpoint, surface a clear error rather than hanging.
      throw (e instanceof Error ? e : new Error("Upload failed — please try again."));
    }
    if (!ticket.direct) return MediaApi.upload(file, onProgress); // local dev / provider without direct upload

    // PUT the whole file straight to the V4 signed URL (single request, XML API object endpoint). No
    // auth header — the signature in the URL authorizes it. Content-Type isn't a signed header, so the
    // browser may send the file's own type; it becomes the stored object's content type.
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", ticket.uploadUrl);
      xhr.setRequestHeader("Content-Type", ticket.contentType || file.type || "application/octet-stream");
      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = (ev) => { if (ev.lengthComputable && ev.total) onProgress(ev.loaded / ev.total); };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed (${xhr.status}).`));
      };
      xhr.onerror = () => reject(new Error("Upload failed — check your connection and try again."));
      xhr.ontimeout = () => reject(new Error("Upload timed out — the connection may be too slow."));
      xhr.send(file);
    });
    return { url: ticket.url, name: ticket.name || file.name, contentType: ticket.contentType };
  },
};

export const MessagesApi = {
  // On-demand voice-note transcription (cached server-side; shown only to the requester).
  transcript: (id) => api.post(`/api/messages/${id}/transcript`),
};

export const PollsApi = {
  // Channel polls (/poll). State lives in the message metadata; votes broadcast MessageEdited.
  create: (channelId, question, options) => api.post("/api/polls", { channelId, question, options }),
  vote: (id, optionIndex) => api.post(`/api/polls/${id}/vote`, { optionIndex }),
  close: (id) => api.post(`/api/polls/${id}/close`),
  pinned: (channelId) => api.get(`/api/polls/pinned?channelId=${channelId}`), // { messages }
};

export const PinsApi = {
  // The channel's pins: polls (top-right rail) + chat messages (the ⋯ menu's "Pinned messages").
  list: (channelId) => api.get(`/api/pins?channelId=${channelId}`), // { messages }
  pin: (id) => api.post(`/api/pins/${id}`),              // for everyone — broadcasts MessageEdited
  unpin: (id) => api.del(`/api/pins/${id}`),             // pinner / author / staff — gone for everyone
};

export const IdeasApi = {
  // Channel ideas (/idea). Like polls, the whole idea lives in the message metadata and every
  // change broadcasts MessageEdited, so the one card in the chat is always current.
  // productChannelId: which product an idea raised outside a product channel is about (null = internal).
  create: (channelId, title, description, productChannelId = null) =>
    api.post("/api/ideas", { channelId, title, description, productChannelId }),
  back: (id) => api.post(`/api/ideas/${id}/back`),
  contribute: (id, text) => api.post(`/api/ideas/${id}/contribute`, { text }),
  removeContribution: (id, contributionId) => api.del(`/api/ideas/${id}/contribute/${contributionId}`),
  close: (id) => api.post(`/api/ideas/${id}/close`),
  reopen: (id) => api.post(`/api/ideas/${id}/reopen`),
  // Staff: AI reads the idea + discussion and proposes stories (creates nothing) → { summary, stories }.
  plan: (id) => api.post(`/api/ideas/${id}/plan`),
  // Staff: create the reviewed stories → { stories, targetChannelId, platformId }.
  promote: (id, body) => api.post(`/api/ideas/${id}/promote`, body),
};

export const CommandsApi = {
  createBug: (body) => api.post("/api/commands/bug", body),
  createFeature: (body) => api.post("/api/commands/feature", body),
  createStory: (body) => api.post("/api/commands/story", body),
};

export const UsersApi = {
  staff: () => api.get("/api/users/staff"), // [{ id, name }] — assignable TMC users
  birthdaysToday: () => api.get("/api/users/birthdays"), // { names } — staff with a birthday today (SAST)
  onLeave: () => api.get("/api/users/on-leave"), // [{ userId, until: "YYYY-MM-DD" }] — approved leave today
};

export const PushApi = {
  key: () => api.get("/api/push/key"),
  subscribe: (sub) => api.post("/api/push/subscribe", sub),
  unsubscribe: (sub) => api.post("/api/push/unsubscribe", sub),
};

export const PrefsApi = {
  // Notification toggles: { notifyMessages, notifyMentions }.
  getNotifications: () => api.get("/api/preferences/notifications"),
  setNotifications: (prefs) => api.put("/api/preferences/notifications", prefs),
  // Staff date of birth (drives the Dream Team birthday banner). { birthDate: "YYYY-MM-DD" | null }
  getBirthday: () => api.get("/api/preferences/birthday"),
  setBirthday: (birthDate) => api.put("/api/preferences/birthday", { birthDate }),
};

export const SignoffsApi = {
  get: (docId) => api.get(`/api/signoffs/${docId}`),
  sign: (docId, channelId) => api.post(`/api/signoffs/${docId}/sign`, { channelId }),
};

export const DeliverablesApi = {
  // Marketing approve/comment cards. id = the Adscope deliverable id (string).
  get: (id) => api.get(`/api/deliverables/${encodeURIComponent(id)}`),
  approve: (id, channelId) => api.post(`/api/deliverables/${encodeURIComponent(id)}/approve`, { channelId }),
  comment: (id, channelId, text) => api.post(`/api/deliverables/${encodeURIComponent(id)}/comment`, { channelId, text }),
};

export const CampaignApi = {
  // Marketing Campaign Builder. The brief `doc` is opaque JSON round-tripped between the wizard
  // and TMCHub; only the wizard understands its shape.
  list: (channelId) => api.get(`/api/campaign?channelId=${channelId}`),
  get: (briefId) => api.get(`/api/campaign/${briefId}`),
  generate: (channelId, answers, referenceImages) =>
    api.post("/api/campaign/generate", { channelId, answers, referenceImages }), // referenceImages: [{ url, name }]
  revise: (channelId, briefId, feedback, editedDoc) =>
    api.post("/api/campaign/revise", { channelId, briefId, feedback, editedDoc }),
  apply: (channelId, mode, briefId, doc) =>
    api.post("/api/campaign/apply", { channelId, mode, briefId, doc }),
};

export const StoryboardsApi = {
  // Editable marketing storyboard card. storyId = the Adscope/TMCHub story id (number).
  // scenes = [{ id, prompt, imageUrl, seconds }] — image URLs come from MediaApi.upload.
  submit: (storyId, { channelId, scenes }) =>
    api.post(`/api/storyboards/${encodeURIComponent(storyId)}/submit`, { channelId, scenes }),
};

export const GifsApi = {
  // Server-proxied Giphy (key stays server-side). → { items:[{id,url,preview,width,height}], hasMore }
  trending: (page = 0) => api.get(`/api/gifs/trending?page=${page}`),
  search: (q, page = 0) => api.get(`/api/gifs/search?q=${encodeURIComponent(q)}&page=${page}`),
};

export const PortfolioApi = {
  // Staff-only drill-down: tasks behind a portfolio (clientId) or status (projectId) blocked/overdue cell.
  // → [{ id, name, projectName, assignee, statusText, dueDate, overdueDays, isBug }]
  tasks: ({ clientId, projectId, metric = "blocked", days } = {}) => {
    const qs = new URLSearchParams({ metric });
    if (clientId != null) qs.set("clientId", clientId);
    if (projectId != null) qs.set("projectId", projectId);
    if (days != null) qs.set("days", days);
    return api.get(`/api/portfolio/tasks?${qs.toString()}`);
  },
};

export const MeetingsApi = {
  // body: { channelId, title?, isVideo, scheduledAt?, remindMinutesBefore? }
  create: (body) => api.post("/api/meetings", body),
  get: (code) => api.get(`/api/meetings/${code}`),
  end: (code) => api.post(`/api/meetings/${code}/end`),
  ice: (code) => api.get(`/api/meetings/${code}/ice`),
  transcript: (code) => api.get(`/api/meetings/${code}/transcript`),
  // My upcoming meetings (agenda) across all my channels.
  upcoming: () => api.get("/api/meetings/upcoming"),
  update: (code, body) => api.put(`/api/meetings/${code}`, body),
  cancel: (code) => api.del(`/api/meetings/${code}`),
  // Skip one occurrence of a recurring meeting — moves its slot to the rule's next occurrence.
  skip: (code) => api.post(`/api/meetings/${code}/skip`),
  // Agenda topics — staff manage; everyone sees them (live via the MeetingTopics event).
  topics: (code) => api.get(`/api/meetings/${code}/topics`),
  addTopic: (code, text) => api.post(`/api/meetings/${code}/topics`, { text }),
  editTopic: (code, topicId, text) => api.put(`/api/meetings/${code}/topics/${topicId}`, { text }),
  deleteTopic: (code, topicId) => api.del(`/api/meetings/${code}/topics/${topicId}`),
  clearTopics: (code) => api.del(`/api/meetings/${code}/topics`), // staff: wipe the whole agenda
  tickTopic: (code, topicId, done) => api.post(`/api/meetings/${code}/topics/${topicId}/done`, { done }),
  generateTopics: (code) => api.post(`/api/meetings/${code}/topics/generate`),
  // Create TMCHub stories from a meeting-notes card's proposals (staff, "Create stories" dialog).
  // targetChannelId: the product channel to file them under — needed when the notes are in Dream Team.
  // Staff: a client-facing recap of a meeting-notes card — draft (Claude), then post the edited text.
  draftRecap: (messageId) => api.post(`/api/meetings/notes/${messageId}/recap`),
  postRecap: (messageId, text) => api.post(`/api/meetings/notes/${messageId}/recap/post`, { text }),
  createStories: (messageId, stories, targetChannelId = null) =>
    api.post(`/api/meetings/notes/${messageId}/stories`, { stories, targetChannelId }),
  // audio: multipart upload of mic-recording chunks (transcribed at meeting end).
  // Sent directly via fetch in useMeeting.
};

export const QuestionsApi = {
  // Staff: create TMCHub stories from a /ask answer card's proposals (same dialog as meeting notes).
  // The card is private + transient, so the proposals travel with the request → { created: [{ index, storyId }] }.
  // targetChannelId: the product channel to file them under — needed outside a product channel.
  createStories: (channelId, question, stories, targetChannelId = null) =>
    api.post("/api/questions/stories", { channelId, question, stories, targetChannelId }),
};

export const SearchApi = {
  // Cross-channel search → { results:[{ messageId, channelId, channelName, channelKind, senderId,
  // senderName, type, isBug, ticketCode, text, createdAt }], channelCount }. Scope is role-based
  // server-side (staff = all channels; clients = their channels, Dream Team excluded).
  search: (q, limit = 60) => api.get(`/api/search?q=${encodeURIComponent(q)}&limit=${limit}`),
};

export const InboxApi = {
  waiting: () => api.get("/api/waiting"),     // staff: { threshold, items: [{ channelId, channelName, clientName, messageId, threadRootId, senderName, snippet, since, workingHours }] }
  dismissWaiting: (messageId) => api.post(`/api/waiting/${messageId}/dismiss`), // staff: "No reply needed"
  approvals: () => api.get("/api/approvals"), // [{ kind: signoff|deliverable|poll, channelId, channelName, messageId, title, createdAt }]
};

// Staff: sign-ups waiting for approval (Dream Team "New sign-ups").
export const SignupsApi = {
  list: () => api.get("/api/signups"), // { items: [{ userId, name, email, company, note, createdAt }], clients: [{ id, name }], products: [{ productId, name, clientId, clientName }] }
  approve: (userId, clientId, productIds) => api.post(`/api/signups/${userId}/approve`, { clientId, productIds }),
  reject: (userId) => api.post(`/api/signups/${userId}/reject`),
};

export const SavedApi = {
  list: () => api.get("/api/saved"), // [{ messageId, channelId, channelName, remindAt, remindedAt, savedAt, message }]
  save: (messageId, remindAt = null) => api.post("/api/saved", { messageId, remindAt }),
  unsave: (messageId) => api.del(`/api/saved/${messageId}`),
};

export const ScheduledApi = {
  list: (channelId) => api.get(`/api/scheduled?channelId=${channelId}`), // my pending: [{ id, content, sendAt }]
  create: (channelId, content, sendAt, mentionedUserIds) => api.post("/api/scheduled", { channelId, content, sendAt, mentionedUserIds }),
  cancel: (id) => api.del(`/api/scheduled/${id}`),
};

export const ThreadsApi = {
  get: (rootId) => api.get(`/api/messages/${rootId}/thread`), // { root, replies }
};

export const ChannelsApi = {
  // Images, videos and files shared in the channel, newest first; `before` = id to page older.
  media: (channelId, before = null) => api.get(`/api/channels/${channelId}/media${before ? `?before=${before}` : ""}`),
  list: () => api.get("/api/channels"),
  // Staff: a channel with no TMCHub product behind it ("+ New channel") → { channelId, name, iconUrl, kind }.
  deleteChannel: (channelId) => api.del(`/api/channels/${channelId}`), // staff, custom channels only — gone for everyone
  createChannel: (name, iconUrl, description) => api.post("/api/channels", { name, iconUrl: iconUrl || null, description: description || null }),
  messages: (channelId, before) =>
    api.get(`/api/channels/${channelId}/messages${before ? `?before=${before}` : ""}`),
  deletedRefs: (channelId) => api.get(`/api/channels/${channelId}/deleted-refs`), // { issueIds, taskIds }
  members: (channelId) => api.get(`/api/channels/${channelId}/members`),
  tickets: (channelId, openOnly = false) => api.get(`/api/channels/${channelId}/tickets${openOnly ? "?openOnly=true" : ""}`),
  // Staff-only: selected messages → TMCHub AI → auto-created tickets (results via ai-plan webhook).
  chatPlan: (channelId, messageIds) => api.post("/api/chat/plan", { channelId, messageIds }),
  ticketDetail: (channelId, { issueId, taskId, to } = {}) =>
    api.get(`/api/channels/${channelId}/ticket?${issueId ? `issueId=${issueId}` : `taskId=${taskId}`}${to ? `&to=${encodeURIComponent(to)}` : ""}`),
  // Staff-only: set a ticket/task's TMCHub status (+ mandatory reason for the waiting statuses).
  updateTicketStatus: (channelId, { issueId, taskId, status, reason }) =>
    api.post(`/api/channels/${channelId}/ticket-status`, { issueId, taskId, status, reason }),
  // Staff-only: assign a ticket/task to a TMC user (assignedToUserId 0 unassigns) → { assignedTo, assignedToUserId }.
  assignTicket: (channelId, { issueId, taskId, assignedToUserId }) =>
    api.post(`/api/channels/${channelId}/ticket-assign`, { issueId, taskId, assignedToUserId }),
  // Staff-only: move a ticket (bug/feature/story) to a different product; the chat card relocates via webhook.
  moveTicketProduct: (channelId, { issueId, taskId, targetProductId }) =>
    api.post(`/api/channels/${channelId}/ticket-move-product`, { issueId, taskId, targetProductId }),
  platforms: (channelId) => api.get(`/api/channels/${channelId}/platforms`), // [{ id, name }]
  mentionables: (channelId) => api.get(`/api/channels/${channelId}/mentionables`),
  // Per-user mute toggle (silences push + drops the channel from the unread badge).
  mute: (channelId, muted) => api.post(`/api/channels/${channelId}/mute`, { muted }),
  // Staff: AI-summarise the caller's unread messages in a channel → { summary, count }.
  summarizeUnread: (channelId) => api.post(`/api/channels/${channelId}/summarize-unread`),
  // Staff channel administration
  candidates: (channelId) => api.get(`/api/channels/${channelId}/candidates`),
  addMember: (channelId, userId, displayName) =>
    api.post(`/api/channels/${channelId}/members`, { userId, displayName }),
  removeMember: (channelId, userId) =>
    request(`/api/channels/${channelId}/members/${userId}`, { method: "DELETE" }),
  update: (channelId, body) =>
    request(`/api/channels/${channelId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  // Staff (Dream Team /newproduct): list clients + create a product → new channel for all staff.
  clients: () => api.get("/api/clients"), // [{ id, name }]
  createProduct: (name, clientId) => api.post("/api/channels/products", { name, clientId }),
};
